import { Directive, EventEmitter, HostListener, Input, OnDestroy, Output } from '@angular/core';

@Directive({
  selector: '[commonUtilitiesAppletOnHold]',
  standalone: true,
})
export class OnHoldDirective implements OnDestroy {
  @Input() holdTime = 400; // Hold time in milliseconds
  @Input() onHoldMoveThreshold = 20; // Threshold to distinguish between click and swipe
  @Input() enableVibration = true; // Threshold to distinguish between click and swipe
  @Input() vibrationDuration = 40; // Threshold to distinguish between click and swipe
  @Output() handleOnHold: EventEmitter<Event | null> = new EventEmitter();

  private timeoutId: any;
  private isHolding = false;
  private startEvent: Event | null = null;
  private startX = 0; // Track the start position (X)
  private startY = 0; // Track the start position
  private moved = false; // Flag to indicate if the mouse has moved

  @HostListener('mousedown', ['$event'])
  @HostListener('touchstart', ['$event'])
  @HostListener('pointerdown', ['$event'])
  onHoldStart(event: MouseEvent | TouchEvent): void {
    if (this.startEvent) {
      return;
    }
    this.isHolding = true;
    this.moved = false; // Reset the moved flag on new interaction
    this.startEvent = event; // Store the start event to use later
    this.clearTimeout();

    // Capture the starting position
    if (event instanceof MouseEvent) {
      this.startX = event.clientX;
      this.startY = event.clientY;
    } else if (event instanceof TouchEvent && event.touches.length > 0) {
      this.startX = event.touches[0].clientX;
      this.startY = event.touches[0].clientY;
    }

    this.timeoutId = setTimeout(() => {
      if (this.isHolding && !this.moved) {
        if (this.enableVibration && 'vibrate' in navigator) {
          navigator.vibrate(this.vibrationDuration);
        }
        this.handleOnHold.emit(this.startEvent); // Emit the stored start event
      }
    }, this.holdTime);

    // event.preventDefault(); // Prevent the default behavior
  }

  @HostListener('touchmove', ['$event'])
  @HostListener('mousemove', ['$event'])
  onMouseMove(event: MouseEvent | TouchEvent): void {
    if (!this.isHolding) return;
    if (!this.startEvent) {
      this.startEvent = event;
    }
    let deltaX = 0;
    let deltaY = 0;
    if (event instanceof MouseEvent) {
      deltaX = Math.abs(event.clientX - this.startX);
      deltaY = Math.abs(event.clientY - this.startY);
    } else if (event instanceof TouchEvent && event.touches.length > 0) {
      deltaX = Math.abs(event.touches[0].clientX - this.startX);
      deltaY = Math.abs(event.touches[0].clientY - this.startY);
    }

    // If the movement exceeds the threshold, consider it as "swipe" or "drag"
    if (deltaX > this.onHoldMoveThreshold || deltaY > this.onHoldMoveThreshold) {
      this.moved = true;
    }
  }

  @HostListener('mouseup', ['$event'])
  @HostListener('touchend', ['$event'])
  @HostListener('mouseleave')
  @HostListener('touchcancel')
  onHoldEnd(event?: MouseEvent | TouchEvent): void {
    this.isHolding = false;
    this.clearTimeout();
    this.startEvent = null; // Reset the start event
  }

  private clearTimeout(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  ngOnDestroy(): void {
    this.clearTimeout();
  }
}
